import { ErrorMessage, Form, Input } from '@/components';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  AmountUnitMultipliers,
  BigCalculator,
  PRICE_INPUT_MAX_VALUE,
  handleInvalidNumber,
  isValidNumberFormatValues,
} from '@/utils';
import type { InputStatus } from 'antd/es/_util/statusUtils';
import Big from 'big.js';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: FormControlProp<T>;
  errors: FieldErrors;
  disabled?: boolean;
  label?: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  unit: AmountUnit;
  marginBottom?: number;
  labelSize?: FieldLabelSize;
  decimalScale?: number;
  max?: number | null;
  min?: number;
  status?: InputStatus;
  shouldCoexistOnChange?: boolean;
  onChange?: (values: number | null) => void;
};

export const MonetaryInputField = <T extends FieldValues>({
  name,
  control,
  errors,
  disabled,
  label,
  hint,
  placeholder,
  required,
  unit,
  marginBottom,
  labelSize = 'medium',
  decimalScale,
  max = PRICE_INPUT_MAX_VALUE,
  min = PRICE_INPUT_MAX_VALUE * -1,
  status,
  shouldCoexistOnChange,
  onChange,
}: Props<T>) => {
  const amountUnitMultiplier = AmountUnitMultipliers[unit];
  return (
    <Form.Item
      style={{ marginBottom }}
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
    >
      <Controller
        name={name}
        control={control}
        render={({
          field: { value, ref, onChange: fieldOnChange, ...field },
        }) => {
          return (
            <NumericFormat
              {...field}
              customInput={Input}
              status={status}
              getInputRef={ref}
              thousandSeparator=","
              placeholder={placeholder}
              addonAfter={AmountUnitLabelModifier[unit]}
              disabled={disabled}
              decimalScale={decimalScale ?? Math.log10(amountUnitMultiplier)}
              value={
                typeof value === 'number'
                  ? BigCalculator.divide(value, amountUnitMultiplier)
                  : ''
              }
              onBlur={({ target: { value } }) => {
                handleInvalidNumber(value, fieldOnChange);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInvalidNumber(e.currentTarget.value, fieldOnChange);
                }
              }}
              onValueChange={({ floatValue, value }) => {
                if (!isValidNumberFormatValues({ floatValue, value })) {
                  return;
                }

                const formattedFloatValue =
                  typeof floatValue === 'number'
                    ? BigCalculator.multiply(floatValue, amountUnitMultiplier)
                    : null;
                if (shouldCoexistOnChange || !onChange) {
                  fieldOnChange(formattedFloatValue);
                }
                onChange?.(formattedFloatValue);
              }}
              isAllowed={({ floatValue }) => {
                if (floatValue === undefined || Number.isNaN(floatValue)) {
                  return true;
                }

                const compareValue = new Big(floatValue).times(
                  amountUnitMultiplier,
                );

                return compareValue.gte(min) && (!max || compareValue.lte(max));
              }}
            />
          );
        }}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
